import { IconClock, IconQuestionMark } from '@tabler/icons';

import { useDialog } from '#root/hooks';
import { replacePathParams, validFormatDistanceStrict } from '#root/utils';
import { ShopperShippingDialog } from '#root/components';
import { Badge, Button, Card, Notice, PriceTotal } from '#root/components/ui';
import { OneLogoSm } from '#root/assets';
import { USER_ORDERS_BY_ID } from '#root/constants/navigation';
import { OrderContent, OrderHeader, OrderProduct } from '../OrderLayout';
import { type OneOrder, assigningText, oneTotals } from './oneOrder';
import OneTotalDialog from './OneTotalDialog';

const OrderStatus = {
  creado: 'CREADO',
  aprobado: 'APROBADO',
} as const;

interface OneOrderCardProps {
  order: OneOrder;
}

/**
 * Tarjeta de un pedido ONE en Mis Pedidos → Pedidos Abiertos (estados CREADO y APROBADO).
 * El pedido ya está pagado: muestra el total pagado y en qué va la asignación.
 */
const OneOrderCard = ({ order }: OneOrderCardProps) => {
  const shippingDialog = useDialog();
  const totalDialog = useDialog();
  const totals = oneTotals(order);

  return (
    <Card noHover>
      <OrderContent>
        <OrderHeader
          number={order.id_public}
          badges={
            <>
              <Badge variant="one" size="sm" aria-label="Pedido ONE">
                <img src={OneLogoSm} alt="ONE" />
              </Badge>
              {Number(order.flag_envio) === 1 && (
                <Badge
                  variant="shipping"
                  size="sm"
                  onClick={shippingDialog.openDialog}
                  icon={<IconQuestionMark size={14} />}>
                  Envío Nacional
                </Badge>
              )}
            </>
          }
          meta={<span>Hace {validFormatDistanceStrict(order.feccre)}</span>}
        />
        <OrderProduct
          title={order.tit}
          url={order.url}
          host={order.url_host}
          image={order.img_path}
          quantity={order.can}
          detail={order.obs}
          chips={[order.condicion, order.empaque]}
          price={
            // Total pagado: abre su desglose
            totals && (
              <PriceTotal
                label="Total pagado"
                amount={totals.total}
                aria-label="Ver detalle del total pagado"
                onClick={totalDialog.openDialog}
              />
            )
          }
          action={
            <Button
              variant="outline"
              size="sm"
              to={replacePathParams(USER_ORDERS_BY_ID, { idp: order.idp })}>
              Ver detalles
            </Button>
          }
        />
      </OrderContent>

      {order.estado === OrderStatus.creado && (
        <Notice tone="info" strip>
          Validando el pago por parte de Guvery...
        </Notice>
      )}
      {order.estado === OrderStatus.aprobado && (
        // Verde y con reloj: el pedido va bien, solo falta asignarlo
        <Notice tone="success" strip icon={<IconClock size={20} />}>
          {assigningText(order)}
        </Notice>
      )}

      <ShopperShippingDialog open={shippingDialog.open} onClose={shippingDialog.closeDialog} />
      {totals && (
        <OneTotalDialog
          open={totalDialog.open}
          onClose={totalDialog.closeDialog}
          title={order.tit}
          totals={totals}
        />
      )}
    </Card>
  );
};

export default OneOrderCard;
